import { DEFAULT_TIMEZONE } from '@app/shared';
import { formatDateInTz } from '../../domain/datetime';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { Alert } from '../../ui/feedback';
import { SectionTitle } from '../../ui/Screen';

/**
 * «Запись недоступна» — шторка локального и глобального бана (ТЗ §14.2).
 *
 * Шторка, а не отдельный экран: бан обнаруживается в момент попытки записаться, и увести
 * человека с наполовину заполненной формы на другой экран значит потерять выбранные услугу,
 * специалиста и слот. Открывается она и из «Моих записей» по ссылке из баннера о неявке —
 * тогда без конкретного ограничения, одними правилами.
 *
 * Дата снятия и причина приходят из отказа API: `err.globallyBanned` и `err.blacklisted`
 * несут `details.until` (см. `ANTISPAM_CHECKS` на бэкенде). Отдельного эндпоинта «мои
 * ограничения» в контракте нет, и выдумывать его фронтенд не вправе.
 */
export type Restriction =
  | {
      readonly kind: 'global';
      /**
       * ISO-момент снятия или `null`.
       *
       * `null` — это БЕССРОЧНО, а не «неизвестно»: `banned_until IS NULL` при поднятом
       * флаге означает бан без срока (ТЗ §8). Показать здесь дату наугад значило бы
       * пообещать снятие, которого не будет.
       */
      readonly until: string | null;
    }
  | {
      readonly kind: 'local';
      /** `null` = бессрочно, как `blacklists.expires_at IS NULL` (ТЗ §8). */
      readonly until: string | null;
      /** Название обязательно: «запись в «—» недоступна» ничего клиенту не объясняет. */
      readonly businessName: string;
    };

export function RestrictionSheet(props: {
  open: boolean;
  restriction: Restriction | null;
  onClose: () => void;
}): JSX.Element {
  const { t } = useI18n();

  return (
    <BottomSheet open={props.open} title={t(props.restriction ? 'banTitle' : 'profRules')} onClose={props.onClose}>
      <div className="mb-3 text-[12.5px] text-ink-3">{t('banSub')}</div>

      {props.restriction ? <RestrictionAlert restriction={props.restriction} /> : null}

      <SectionTitle className="mt-4">{t('banRulesT')}</SectionTitle>
      {(['banRule1', 'banRule2', 'banRule3', 'banRule4', 'banRule5'] as const).map((key) => (
        <div key={key} className="flex gap-2 py-1.5 text-[13px] leading-[1.45] text-ink-2">
          <span className="shrink-0 text-accent">▸</span>
          <span>{t(key)}</span>
        </div>
      ))}

      <div className="mt-3.5">
        <Alert>{t('banKeep')}</Alert>
        <Alert>{t('banHelp')}</Alert>
      </div>
    </BottomSheet>
  );
}

function RestrictionAlert(props: { restriction: Restriction }): JSX.Element {
  const { t, lang } = useI18n();
  const { restriction } = props;

  // Дата снятия рисуется в поясе Узбекистана: пояс конкретной организации к глобальному
  // бану отношения не имеет, а `DEFAULT_TIMEZONE` — заявленный пояс продукта (@app/shared),
  // а не догадка этого файла.
  const date = restriction.until
    ? formatDateInTz(restriction.until, DEFAULT_TIMEZONE, lang)
    : null;

  if (restriction.kind === 'global') {
    return (
      <Alert tone="danger" title={t('banGlobalT')}>
        {date ? t('banGlobalB', { date }) : t('banGlobalForever')}
      </Alert>
    );
  }

  return (
    <Alert tone="danger" title={t('banLocalT')}>
      {date
        ? t('banLocalB', { biz: restriction.businessName, date })
        : t('banLocalForever', { biz: restriction.businessName })}
    </Alert>
  );
}

/** Отказ API → ограничение для шторки. `null`, если отказ к ограничениям не относится. */
export function restrictionFromFailure(
  key: string,
  details: Readonly<Record<string, string | number>> | undefined,
  businessName: string,
): Restriction | null {
  const until = typeof details?.['until'] === 'string' ? (details['until'] as string) : null;
  if (key === 'err.globallyBanned') return { kind: 'global', until };
  if (key === 'err.blacklisted') return { kind: 'local', until, businessName };
  return null;
}
